import {
  Avatar,
  Button,
  Card,
  ChevronDownIcon,
  ChevronUpIcon,
  Image,
  majorScale,
  Pane,
  Pill,
  Spinner,
  Text
} from 'evergreen-ui'
import Parser from 'html-react-parser'
import { FC, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Lightbox from 'yet-another-react-lightbox'
import { Rating, UserComment } from '../../types'
import { convertToFriendlyDate } from '../../utils/convert-date'
import { generateFullName, generateUsername } from '../../utils/username'
import {
  ContentEditorDetails,
} from '../ContentEditor/ContentEditor'
import EditableContextMenu from '../EditableContextMenu/EditableContextMenu'
import PostCommentBox from '../PostCommentBox/PostCommentBox'
import RateButton from '../RateButton/RateButton'
import WysiwygEditor from '../WysiwygEditor/WysiwygEditor'
import styles from './CommentBox.module.scss'

interface CommentBoxProps {
  comment: UserComment
  isReply: boolean
  showActionPane?: boolean
  isPostingComment: boolean
  isPostSuccess: boolean
  isAuthor: boolean
  isLoggedInUser: boolean
  isSavingRating: boolean
  isEditSaveSuccess: boolean
  isSavingEdit: boolean
  postComment: (comment: string, isReply: boolean, parentId: string) => void
  rateComment: (rating: number, ratingId?: string) => void
  editComment: (comment: ContentEditorDetails) => void
  setCommentUploads: (uploads: File[]) => void
  nestingLevel?: number
  maxNestingLevel?: number
  parentComment?: UserComment
}

const CommentBox: FC<CommentBoxProps> = (props: CommentBoxProps) => {
  const [showReplyContainer, setShowReplyContainer] = useState(false)
  const [rating, setRating] = useState<Rating | undefined>(undefined)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [lightboxIndex, setLightboxIndex] = useState<number>(-1)

  const { pathname, hash, key } = useLocation()
  
  // Calculate nesting level and indentation
  const nestingLevel = props.nestingLevel || 0
  const maxNestingLevel = props.maxNestingLevel || 10
  const shouldShowReplyButton = nestingLevel < maxNestingLevel
  const indentation = majorScale(nestingLevel * 2)

  // scroll to comment
  useEffect(() => {
    // if not a hash link, scroll to top
    if (hash === '') {
      window.scrollTo(0, 0)
    }
    // else scroll to id
    else {
      setTimeout(() => {
        const id = hash.replace('#', '')
        const element = document.getElementById(id)
        if (element) {
          element.scrollIntoView()
          element.style.border = '3px solid #0F5156'
        }
      }, 0)
    }
  }, [pathname, hash, key]) // do this on route change

  useEffect(() => {
    if (props.isPostSuccess) {
      setShowReplyContainer(false)
    }
  }, [props.isPostSuccess])

  useEffect(() => {
    if (props.isEditSaveSuccess) {
      setIsEditing(false)
    }
  }, [props.isEditSaveSuccess])

  useEffect(() => {
    if (props.comment.ratings) {
      const rating = props.comment.ratings.find(
        (r) => r.comment_id === props.comment.id,
      )

      if (rating) {
        setRating(rating)
      }
    }
  }, [props.comment])

  return (
    <Pane
      id={props.comment.id}
      marginBottom={majorScale(2)}
      borderRadius={10}
      marginLeft={indentation}
      position="relative"
    >
      {/* Visual connection line for replies */}
      {nestingLevel > 0 && (
        <Pane
          position="absolute"
          left={-majorScale(4)}
          top={majorScale(2)}
          width={majorScale(4)}
          height="1px"
          background="#E4E7EB"
          zIndex={1}
        />
      )}
      
      {/* Vertical line connecting to parent */}
      {nestingLevel > 0 && (
        <Pane
          position="absolute"
          left={-majorScale(4)}
          top={0}
          bottom={majorScale(2)}
          width="1px"
          background="#E4E7EB"
          zIndex={1}
        />
      )}

      <Card
        background={nestingLevel > 0 ? '#FAFBFC' : 'white'}
        className={props.isReply ? styles.ReplyBox : styles.CommentBox}
        elevation={1}
        borderRadius={10}
        overflow="hidden"
        style={{
          borderLeft: nestingLevel > 0 ? `4px solid #E4E7EB` : 'none',
          marginLeft: nestingLevel > 0 ? majorScale(1) : 0,
        }}
      >
        <Pane display="flex" flexDirection="column" padding={majorScale(2)}>
          <Pane
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Pane display="flex" alignItems="center">
              <Avatar
                name={generateFullName(props.comment.user)}
                color="blue"
                marginRight={majorScale(1)}
              ></Avatar>
              <Pane display="flex" alignItems="center" justifyContent="center">
                <Text>
                  <Text display="flex" alignItems="center">
                    <Text fontWeight="bold" marginRight={majorScale(1)} fontSize={'0.9rem'}>
                      {generateUsername(props.comment.user)}
                    </Text>
                    {props.isAuthor ? <Pill color="teal">Author</Pill> : null}
                  </Text>
                  <Text fontSize={'0.8rem'} color="gray700">
                    {convertToFriendlyDate(props.comment.created_at, true)}
                    {nestingLevel > 0 && props.parentComment && (
                      <Text color="gray500" marginLeft={majorScale(1)}>
                        • replying to {generateUsername(props.parentComment.user)}
                      </Text>
                    )}
                  </Text>
                </Text>
              </Pane>
            </Pane>
            {/* Only show the edit menu if it is the logged in user */}
            {props.isLoggedInUser ? (
              <EditableContextMenu
                edit={() => {
                  setIsEditing(!isEditing)
                }}
              ></EditableContextMenu>
            ) : null}
          </Pane>

          <Pane
            marginTop={majorScale(2)}
            paddingLeft={majorScale(2)}
            display="flex"
            width="100%"
          >
            {isEditing ? (
              <WysiwygEditor
                initialValue={props.comment.comment}
                onUpdateContent={(newContent: string) => {
                  props.editComment({content: newContent})
                  setIsEditing(false)
                }}
              />
            ) : (
              <Text
                fontSize={'1rem'}
                whiteSpace="pre-line"
                lineHeight={1.5}
              >
                {Parser(props.comment.comment)}
              </Text>
            )}
          </Pane>
          {(props.comment.userUploads?.length ?? 0) > 0 ? (
              <Pane
                display="flex"
                flexDirection="row"
                gap={majorScale(2)}
                marginBottom={majorScale(2)}
              >
                {props.comment.userUploads?.map((upload, index) => (
                  <Pane
                    elevation={1}
                    key={upload.id}
                    hoverElevation={2}
                    cursor={'pointer'}
                    onClick={() => setLightboxIndex(index)}
                  >
                    <Image
                      className={styles.Thumbnail}
                      src={upload.url}
                      width={100}
                      height={100}
                    ></Image>
                  </Pane>
                ))}
              </Pane>
            ) : null}
        </Pane>

        <Pane
          display={props.showActionPane === false ? 'none' : 'flex'}
          alignItems={'center'}
          justifyContent={props.isReply ? 'flex-start' : 'space-between'}
          alignContent={'flex-end'}
          background={'gray50'}
          padding={majorScale(1)}
        >
          {props.isSavingRating ? (
            <Spinner size={32} padding={5} />
          ) : (
            <Pane>
              <RateButton
                onClick={(val) => props.rateComment(val, rating?.id)}
                value={1}
                icon={<ChevronUpIcon />}
                isActiveVote={rating?.is_upvote}
                count={props.comment.meta?.upvotes ?? 0}
                activeColor={'green'}
              ></RateButton>
              <RateButton
                onClick={(val) => props.rateComment(val, rating?.id)}
                value={-1}
                icon={<ChevronDownIcon />}
                isActiveVote={rating?.is_downvote}
                count={props.comment.meta?.downvotes ?? 0}
                activeColor={'neutral'}
              ></RateButton>
            </Pane>
          )}
          <Button
            is="button"
            appearance="minimal"
            color="#3366FF"
            fontWeight="bold"
            display={shouldShowReplyButton ? 'inherit' : 'none'}
            onClick={() => setShowReplyContainer(!showReplyContainer)}
          >
            Reply
          </Button>
        </Pane>
      </Card>
      <Pane marginLeft={majorScale(4)}>
        <PostCommentBox
          show={showReplyContainer}
          isPosting={props.isPostingComment}
          isPostSuccess={props.isPostSuccess}
          postComment={(comment) =>
            props.postComment(comment, true, props.comment.id)
          }
          setCommentUploads={props.setCommentUploads}
          postButtonText={'Post Reply'}
          placeholderText={'Write something...'}
        ></PostCommentBox>
      </Pane>

      {/* Recursively render replies */}
      {props.comment.replies && props.comment.replies.length > 0 && (
        <Pane marginTop={majorScale(1)}>
          {props.comment.replies.map((reply) => (
            <CommentBox
              key={reply.id}
              comment={reply}
              isReply={true}
              isPostingComment={props.isPostingComment}
              isPostSuccess={props.isPostSuccess}
              isAuthor={props.isAuthor}
              isLoggedInUser={props.isLoggedInUser}
              isSavingRating={props.isSavingRating}
              isEditSaveSuccess={props.isEditSaveSuccess}
              isSavingEdit={props.isSavingEdit}
              postComment={props.postComment}
              rateComment={props.rateComment}
              editComment={props.editComment}
              setCommentUploads={props.setCommentUploads}
              nestingLevel={nestingLevel + 1}
              maxNestingLevel={maxNestingLevel}
              parentComment={props.comment}
            />
          ))}
        </Pane>
      )}


      <Lightbox
        open={lightboxIndex > -1}
        index={lightboxIndex}
        close={() => setLightboxIndex(-1)}
        slides={props.comment.userUploads?.map((u) => {
          return { src: u.url }
        })}
      ></Lightbox>
    </Pane>
  )
}

export default CommentBox
